import { createBrowserRouter, Navigate, redirect } from "react-router-dom";
import { lazy } from "react";
import { RouterProps } from "./typing";
import Layout from "@/layout";
import NotFound from "@/views/error/404";
import Error from "@/views/error/500";
import { AUTH_MODE, hasAuthSession } from "@/utils/auth";
import SearchParamsRoot from "@/routing/SearchParamsRoot";

// Page-level components are unpacked according to routes to reduce first-screen business bundles.
const Login = lazy(() => import("@/views/login"));
const Users = lazy(() => import("@/views/users"));
const UsersDetail = lazy(() => import("@/views/users/detail"));
const UsersReviews = lazy(() => import("@/views/users/reviews"));
const UsersTransaction = lazy(() => import("@/views/users/transaction"));
const UserReferral = lazy(() => import("@/views/users/referral"));
const DashBoard = lazy(() => import("@/views/dashboard"));
const Gyms = lazy(() => import("@/views/gyms"));
const GymsDetail = lazy(() => import("@/views/gyms/detail"));
const GymsReviews = lazy(() => import("@/views/gyms/reviews"));
const GymsPasses = lazy(() => import("@/views/gyms/passes"));
const GymsReferral = lazy(() => import("@/views/gyms/referral"));
const GymInvitations = lazy(() => import("@/views/gyms/invitations"));
const Contact = lazy(() => import("@/views/contact"));
const Version = lazy(() => import("@/views/version"));
const Account = lazy(() => import("@/views/account"));
const Payout = lazy(() => import("@/views/payout"));
const PayoutInvoice = lazy(() => import("@/views/payout/invoice"));
const PayoutGroup = lazy(() => import("@/views/payout/group"));
const PayoutRecord = lazy(() => import("@/views/payout/record"));
const GuardianshipList = lazy(() => import("@/views/guardianship"));
const Campaigns = lazy(() => import("@/views/campaigns"));
const CampaignsDetails = lazy(() => import("@/views/campaigns/detail"));
const GuardianshipDetails = lazy(() => import("@/views/guardianship/detail"));
const CampaignsCreate = lazy(() => import("@/views/campaigns/craete"));
const UserGuardianship = lazy(() => import("@/views/users/guardianship"));
const DeleteAppAccount = lazy(() => import("@/views/delete-app-account"));
const ClassDetailPage = lazy(() => import("@/views/gyms/class-detail"));
const Informations = lazy(() => import("@/views/informations"));
const ClassSessions = lazy(() => import("@/views/gyms/class-detail/ClassSessions"));
const ClassReviews = lazy(() => import("@/views/gyms/class-detail/ClassReviews"));
const WellnessDetailPage = lazy(() => import("@/views/gyms/detail/Wellness/wellness-detail"));
const WellnessSessions = lazy(() => import("@/views/gyms/detail/Wellness/wellness-detail/WellnessSessions"));
const WellnessReviews = lazy(() => import("@/views/gyms/detail/Wellness/wellness-detail/WellnessReviews"));
const ReferralBonusSettings = lazy(() => import("@/views/referral-bonus-settings"));

//Complete authentication during the route loading phase, preventing Layout and subpages from rendering first and then jumping.
const authLoader = () => {
  if (!hasAuthSession()) throw redirect("/login");
  return null;
};

// Logged-in users no longer enter the login page.
const publicOnlyLoader = () => {
  // Cookie mode cannot read HttpOnly Cookie synchronously. The login page will be released first. Later, the lightweight session interface can be used to improve the logged-in access experience.
  if (AUTH_MODE !== "cookie" && hasAuthSession()) throw redirect("/dashboard");
  return null;
};

export const routerList: RouterProps[] = [
  {
    element: <Layout />,
    loader: authLoader,
    errorElement: <Error />,
    children: [
      {
        path: "dashboard",
        element: <DashBoard />,
        handle: { menu: true, menuTitle: "Dashboard" }
      },
      {
        path: "gyms",
        handle: { menu: true, menuTitle: "Businesses" },
        children: [
          {
            index: true,
            element: <Gyms />
          },
          {
            path: "details/:businessId",
            element: <GymsDetail />
          },
          {
            path: "reviews/:businessId",
            element: <GymsReviews />
          },
          {
            path: "passes/:businessId",
            element: <GymsPasses />
          },
          {
            path: "referral/:businessId",
            element: <GymsReferral />
          },
          {
            path: "invitations/:businessId",
            element: <GymInvitations />
          }
        ]
      },
      // class detail pages
      {
        path: "class-detail/:businessId/:classId",
        element: <ClassDetailPage />
      },
      {
        path: "class-detail/session/:businessId/:classId",
        element: <ClassSessions />
      },
      {
        path: "class-detail/review/:businessId/:classId",
        element: <ClassReviews />
      },
      // wellness detail pages
      {
        path: "wellness-detail/:businessId/:wellnessId",
        element: <WellnessDetailPage />
      },
      {
        path: "wellness-detail/session/:businessId/:wellnessId",
        element: <WellnessSessions />
      },
      {
        path: "wellness-detail/review/:businessId/:wellnessId",
        element: <WellnessReviews />
      },
      {
        path: "users",
        handle: { menu: true, menuTitle: "Users" },
        children: [
          {
            index: true,
            element: <Users />
          },
          {
            path: "details/:accountId",
            element: <UsersDetail />
          },
          {
            path: "reviews/:accountId",
            element: <UsersReviews />
          },
          {
            path: "transaction/:accountId",
            element: <UsersTransaction />
          },
          {
            path: "referral/:accountId",
            element: <UserReferral />
          },
          {
            path: "guardianship/:accountId",
            element: <UserGuardianship />
          }
        ]
      },
      {
        path: "payout",
        handle: { menu: true, menuTitle: "Payout" },
        children: [
          {
            index: true,
            element: <Payout />
          },
          {
            path: "invoice",
            element: <PayoutInvoice />
          },
          {
            path: "invoice/:weekNo",
            element: <PayoutGroup />
          },
          {
            path: "record/:businessId",
            element: <PayoutRecord />
          }
        ]
      },
      {
        path: "contact",
        element: <Contact />
      },
      {
        path: "version",
        element: <Version />,
        handle: { menu: true, menuTitle: "Version" }
      },
      {
        path: "account",
        element: <Account />
      },
      {
        path: "referral-bonus-settings",
        element: <ReferralBonusSettings />
      },
      {
        path: "informations",
        element: <Informations />
      },
      {
        path: "guardianship",
        children: [
          {
            index: true,
            element: <GuardianshipList />
          },
          { path: "details/:id", element: <GuardianshipDetails /> }
        ]
      },
      {
        path: "campaigns",
        children: [
          {
            index: true,
            element: <Campaigns />
          },
          { path: "details/:id", element: <CampaignsDetails /> },
          { path: "create", element: <CampaignsCreate /> }
        ]
      }
    ]
  },
  {
    path: "/login",
    element: <Login />,
    loader: publicOnlyLoader
  },
  {
    path: "/delete-account",
    element: <DeleteAppAccount />
  },
  {
    path: "/404",
    element: <NotFound />
  },
  {
    path: "/",
    element: <Navigate to="/dashboard" replace />
  },
  {
    path: "*",
    element: <Navigate to="/404" replace />
  }
];

// Typed URL state is read through the router location, so the adapter wraps the whole tree from the
// inside. Paths and the basename are unchanged: the wrapper route has no path of its own.
export const routeTree: RouterProps[] = [{ element: <SearchParamsRoot />, children: routerList }];

const routes = createBrowserRouter(routeTree, { basename: import.meta.env.BASE_URL });
export default routes;
